import { fetchApi } from './api';
import type {
    VisitReportSalesperson,
    VisitReportCustomerOption,
    VisitReportDraft,
    VisitReportSummary,
    VisitReportRecord,
    VisitReportWizardStep,
    TranscribeResponse,
    InterviewMessage,
    InterviewResponse,
    SyncHubspotResponse,
    HubspotPreview,
    ActivityTypesResponse,
    VisitReportDealsResponse,
    MarketObservation,
    MarketObservationOptionsResponse,
    MarketdataPlanResponse,
    MarketdataWriteResponse,
    VisitReportCategoryPickerItem,
    VisitReportProductPickerItem,
} from '../types/visitReport';

export interface TranscribeRequest {
    audioBase64: string;
    mimeType: string;
    fileName?: string;
    languageHint?: string;
    vocabularyPrompt?: string;
}

export interface InterviewRequest {
    hubspotCompanyId: string;
    customerId?: string;
    companyCode?: string;
    transcript?: string;
    currentDraft?: VisitReportDraft;
    conversationHistory?: InterviewMessage[];
    userMessage?: string;
    language?: string;
    salesRepName?: string;
}

export interface ExtractUploadRequest {
    fileBase64: string;
    mimeType: string;
    fileName?: string;
    languageHint?: string;
    companyName?: string;
}

export interface ExtractUploadResponse {
    extractedText: string;
    fileKind: 'image' | 'pdf' | 'docx';
    byteSize: number;
    fileName: string | null;
}

export interface SaveDraftRequest {
    id?: string;
    hubspotCompanyId: string;
    customerId?: string;
    companyCode?: string;
    hubspotDealId?: string;
    visitDate?: string;
    language?: string;
    rawTranscript?: string;
    draft: VisitReportDraft;
    conversationHistory?: InterviewMessage[];
    wizardStep?: VisitReportWizardStep;
}

export const visitReportsApi = {
    transcribe(req: TranscribeRequest): Promise<TranscribeResponse> {
        return fetchApi<TranscribeResponse>('/visit-reports/transcribe', {
            method: 'POST',
            body: JSON.stringify(req),
        });
    },

    interview(req: InterviewRequest): Promise<InterviewResponse> {
        return fetchApi<InterviewResponse>('/visit-reports/interview', {
            method: 'POST',
            body: JSON.stringify(req),
        });
    },

    extractUpload(req: ExtractUploadRequest): Promise<ExtractUploadResponse> {
        return fetchApi<ExtractUploadResponse>('/visit-reports/extract-upload', {
            method: 'POST',
            body: JSON.stringify(req),
        });
    },

    saveDraft(req: SaveDraftRequest): Promise<{ id: string; createdAt?: string; updatedAt: string }> {
        return fetchApi<{ id: string; createdAt?: string; updatedAt: string }>('/visit-reports', {
            method: 'POST',
            body: JSON.stringify(req),
        });
    },

    listDrafts(status?: 'draft' | 'synced' | 'discarded' | 'all', hubspotCompanyId?: string): Promise<VisitReportSummary[]> {
        const qs = new URLSearchParams();
        if (status) qs.set('status', status);
        if (hubspotCompanyId) qs.set('hubspotCompanyId', hubspotCompanyId);
        const query = qs.toString();
        return fetchApi<VisitReportSummary[]>(`/visit-reports${query ? '?' + query : ''}`);
    },

    getDraft(id: string): Promise<VisitReportRecord> {
        return fetchApi<VisitReportRecord>(`/visit-reports/${encodeURIComponent(id)}`);
    },

    discardDraft(id: string): Promise<void> {
        return fetchApi<void>(`/visit-reports/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        });
    },

    syncHubspot(id: string, activityType?: string): Promise<SyncHubspotResponse> {
        return fetchApi<SyncHubspotResponse>(`/visit-reports/${encodeURIComponent(id)}/sync-hubspot`, {
            method: 'POST',
            body: JSON.stringify({ activityType }),
        });
    },

    /**
     * What the sync would send. Same server-side builder, so this is the payload and not a
     * second rendering of it — see api/src/functions/visit-reports/hubspot-payload.ts.
     */
    previewHubspot(id: string, activityType?: string): Promise<HubspotPreview> {
        return fetchApi<HubspotPreview>(`/visit-reports/${encodeURIComponent(id)}/preview-hubspot`, {
            method: 'POST',
            body: JSON.stringify({ activityType }),
        });
    },

    /**
     * The company's deals, grouped by pipeline, open first. Read from the DolderDB replica, so
     * it costs no HubSpot call — and lags by one nightly replication.
     */
    listDeals(hubspotCompanyId: string): Promise<VisitReportDealsResponse> {
        return fetchApi<VisitReportDealsResponse>(
            `/visit-reports/deals?companyId=${encodeURIComponent(hubspotCompanyId)}`,
        );
    },

    /** The Activity Types the write portal accepts, read live. Never a hard-coded list. */
    activityTypes(): Promise<ActivityTypesResponse> {
        return fetchApi<ActivityTypesResponse>('/visit-reports/activity-types');
    },

    /**
     * What the market-data section needs: the three live HubSpot enumerations, the Dolder entity
     * a record would be filed under, and the server's verdict on what the AI suggested.
     *
     * A POST for a read because the suggestions travel in the body — one per observation, free
     * text, in seven languages. Called when the rep reaches the review step, like
     * `activityTypes()`, and never during the interview.
     *
     * **`includeOptions` decides whether it touches HubSpot at all.** False, and the answer is
     * the gate alone, straight out of our own database. True, and it also carries the three
     * live enumerations — one property-schema read per hour per env, shared with the Market
     * Intelligence tab. A report with no market data therefore costs nothing, and a report with
     * no ERP customer costs nothing either way.
     */
    marketObservationOptions(
        companyCode: string | undefined,
        observations: MarketObservation[],
        includeOptions: boolean,
    ): Promise<MarketObservationOptionsResponse> {
        return fetchApi<MarketObservationOptionsResponse>('/visit-reports/enum-options', {
            method: 'POST',
            body: JSON.stringify({
                companyCode,
                includeOptions,
                observations: observations.map(o => ({
                    vendorSuggestion: o.vendorSuggestion,
                    itemCategorySuggestion: o.itemCategorySuggestion,
                    currencySuggestion: o.currencySuggestion,
                })),
            }),
        });
    },

    /**
     * What the market-data step would do: N records to create, M to update, with the exact
     * property values and the exact alternatives string per observation.
     *
     * A GET, and it is guarded by `HUBSPOT_ALLOW_WRITE_MARKETDATA` even though it writes
     * nothing (D12). The preview costs HubSpot calls — the property schema and the company's
     * records — and a parked feature must cost none. With the kill switch off this answers 503
     * with zero calls, and the step says so instead of showing a plan nobody could act on.
     *
     * Available only once the report has synced: a record's provenance line points at a report,
     * and a report that never reached the CRM is not something anyone there can look up.
     */
    marketDataPlan(id: string): Promise<MarketdataPlanResponse> {
        return fetchApi<MarketdataPlanResponse>(
            `/visit-reports/${encodeURIComponent(id)}/market-data`,
        );
    },

    /**
     * Write ONE observation. One call per card, acknowledged one at a time (D4).
     *
     * `competitorProduct` travels beside `observationIndex` on purpose: an index is a position
     * and a position is not an identity. If the draft changed after the step was opened the two
     * disagree and the server answers 409 rather than writing the wrong product into the CRM.
     *
     * Repeating the step cannot duplicate what already went through: the server checks its own
     * audit log for this report before creating anything.
     */
    writeMarketData(
        id: string,
        req: { observationIndex: number; competitorProduct: string; action: 'create' | 'update'; marketdataId?: string },
    ): Promise<MarketdataWriteResponse> {
        return fetchApi<MarketdataWriteResponse>(
            `/visit-reports/${encodeURIComponent(id)}/market-data`,
            { method: 'POST', body: JSON.stringify(req) },
        );
    },

    /**
     * The article half of the alternatives picker — blocked articles excluded (D11).
     *
     * A different endpoint from `searchProducts` below, which feeds *products discussed* and
     * deliberately shows blocked articles because it records what was once sold.
     */
    searchAlternativeProducts(companyCode: string, q: string, limit = 50): Promise<{
        items: VisitReportProductPickerItem[]; total: number; companyCode: string; excludeBlocked: boolean;
    }> {
        const qs = new URLSearchParams({ companyCode, limit: String(limit) });
        if (q) qs.set('q', q);
        return fetchApi(`/visit-reports/alt-products?${qs.toString()}`);
    },

    /** The subclass half — the leaf level of etl.ItemCategory. */
    searchAlternativeCategories(companyCode: string, q: string, limit = 50): Promise<{
        items: VisitReportCategoryPickerItem[]; total: number; companyCode: string;
    }> {
        const qs = new URLSearchParams({ companyCode, limit: String(limit) });
        if (q) qs.set('q', q);
        return fetchApi(`/visit-reports/alt-categories?${qs.toString()}`);
    },

    /**
     * The two calls behind `MobileCustomerPicker`. They moved here from `customer360Api` in P9
     * (2026-09-21) with their endpoints; the paths still say `customer-360` on purpose — see
     * the header of `api/src/functions/visit-reports/get-salespeople.ts`.
     */
    getSalespeople(): Promise<VisitReportSalesperson[]> {
        return fetchApi<VisitReportSalesperson[]>('/customer-360/salespeople');
    },

    getCustomers(staffId?: string): Promise<VisitReportCustomerOption[]> {
        const params = staffId ? `?staffId=${encodeURIComponent(staffId)}` : '';
        return fetchApi<VisitReportCustomerOption[]>(`/customer-360/customers${params}`);
    },

    searchProducts(companyCode: string, q: string = '', limit: number = 50): Promise<Array<{ productId: string; productName: string; company: string }>> {
        const qs = new URLSearchParams({ companyCode, limit: String(limit) });
        if (q) qs.set('q', q);
        return fetchApi<Array<{ productId: string; productName: string; company: string }>>(
            `/visit-reports/products?${qs.toString()}`,
        );
    },
};
